import React, { useEffect, useRef } from "react";
import { fmtNumber } from "../../utils/helpers";
import { getAreaDisplayLabel, renderDelta } from "./TableUtils";

const MetroDataTable = ({ 
  currentMetro, 
  selectedMetro, 
  showChart, 
  allDates, 
  latestDate, 
  forecastByName, 
  areaType, 
  linksByName, 
  linksByRegionId,
  setAreaType, 
  setSelectedMetro, 
  setSelectedName, 
  setView, 
  showExplore = true 
}) => {
  const dropdownRef = useRef(null);
  const lastNavRef = useRef({ key: '', ts: 0 });

  // Force a simple log to test if code is updating
  console.log(`[METRO_DATA_TABLE] COMPONENT RENDERED - TEST LOG`);
  console.log(`[METRO_DATA_TABLE] showChart function received:`, typeof showChart);
  console.log(`[METRO_DATA_TABLE] selectedMetro received:`, selectedMetro);

  // Debug: Log currentMetro data to see what fields are available
  if (currentMetro) {
    console.log(`[METRO_DATA_TABLE] currentMetro data:`, {
      Price: currentMetro.Price,
      PriceYoY: currentMetro.PriceYoY,
      Inventory: currentMetro.Inventory,
      InventoryDelta: currentMetro.InventoryDelta,
      f1m: currentMetro.f1m,
      f3m: currentMetro.f3m,
      f12m: currentMetro.f12m,
      allKeys: Object.keys(currentMetro)
    });
  }

  // Debug props
  console.log(`[METRO_DATA_TABLE] renderMetroData called with:`, {
    currentMetro: !!currentMetro,
    selectedMetro,
    showChart: !!showChart,
    allDates: allDates?.length || 0,
    latestDate,
    areaType,
    showChartType: typeof showChart,
    selectedMetroType: typeof selectedMetro
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Allow clicks on dropdown items to proceed without closing
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        const clickedButton = event.target.closest('.dropdown-item');
        if (clickedButton) {
          // Let the button's onClick handler run first, then close
          setTimeout(() => {
            const dropdown = dropdownRef.current?.querySelector('.dropdown-menu');
            if (dropdown) dropdown.style.display = 'none';
          }, 100);
          return;
        }
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const dropdown = dropdownRef.current.querySelector('.dropdown-menu');
        if (dropdown) {
          dropdown.style.display = 'none';
        }
      }
    };

    // Use click instead of mousedown to allow button onClick to fire first
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  console.log("[METRO_DATA_TABLE] renderMetroData called with:", {
    currentMetro,
    hasCurrentMetro: !!currentMetro,
    currentMetroType: typeof currentMetro,
    selectedMetro,
    areaType,
    allDates: allDates?.length || 0,
    latestDate
  });
  
  if (!currentMetro) {
    console.log("[METRO_DATA_TABLE] renderMetroData returning null - currentMetro is falsy");
    console.log("[METRO_DATA_TABLE] Debug info:", {
      currentMetro,
      selectedMetro,
      areaType,
      allDates: allDates?.length || 0,
      latestDate
    });
    
    // Show helpful message for County level when data is missing
    if (areaType === "County") {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          <h3>County Data Not Available</h3>
          <p>No snapshot data found for this county.</p>
          <p>This might be because:</p>
          <ul style={{ textAlign: 'left', display: 'inline-block' }}>
            <li>The county name doesn't match the data</li>
            <li>The county data file is missing or empty</li>
            <li>The data hasn't loaded yet</li>
          </ul>
          <p><strong>Selected:</strong> {selectedMetro || 'Unknown'}</p>
        </div>
      );
    }
    
    return null;
  }
  
  // Helper to open a deeper level and show its data (RegionID-aware)
  const openDataDeep = async (level, name, regionId = null) => {
    try {
      if (setAreaType) setAreaType(level);
      // Immediately select the target by name so the snapshot row updates
      const target = String(name || "").trim();
      // Debounce identical rapid navigations to prevent double snapshot flash
      const navKey = level + '|' + target;
      const nowTs = Date.now();
      if (lastNavRef.current.key === navKey && (nowTs - lastNavRef.current.ts) < 300) {
        return;
      }
      lastNavRef.current = { key: navKey, ts: nowTs };

      // If we're already at the desired level and selection, just ensure view is 'metro'
      if (areaType === level && selectedMetro === target) {
        if (setView) setView('metro');
        return;
      }
      if (setSelectedMetro) setSelectedMetro(target);
      if (setSelectedName) setSelectedName(target);
      const deadline = Date.now() + 2500;
      while (Date.now() < deadline) {
        const has = Array.isArray(allDates) && allDates.some(r => String(r?.Metro || "").trim() === target || (level === 'Zipcode' && String(r?.Metro || "").replace(/\D+/g, '').padStart(5,'0').slice(0,5) === target));
        if (has) break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 100));
      }
      // Removed automatic chart loading - charts should only load when user clicks chart buttons
      // await showChart('Price', target);
      if (setView) setView('metro');
    } catch {}
  };

  const renderExploreCell = () => {
    if (!selectedMetro) return null;
    // RegionID-based match only (no name fallback)
    let link = null;
    const parentRegionId = currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id || null;
    if (parentRegionId && linksByRegionId) {
      link = (
        areaType === 'State' ? linksByRegionId?.State?.[parentRegionId]
        : areaType === 'Metro' ? linksByRegionId?.Metro?.[parentRegionId]
        : areaType === 'City' ? linksByRegionId?.City?.[parentRegionId]
        : areaType === 'County' ? linksByRegionId?.County?.[parentRegionId]
        : areaType === 'Zipcode' ? linksByRegionId?.Zipcode?.[parentRegionId]
        : null
      );
    }
    // Fallback: always show the dropdown UI even if no links are available
    if (!link) {
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ paddingTop: 6, color: '#666' }}>
            No recommended child areas for this selection.
          </div>
        </details>
      );
    }

      const openBtn = (lvl, nm, label, childRegionId = null) => (
        <button
          className="btn"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDataDeep(lvl, String(nm), childRegionId); }}
        >
          {label}
        </button>
      );

    if (areaType === 'State' && link.BestMetro) {
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
            {openBtn('Metro', link.BestMetro, `Metro: ${link.BestMetro}`)}
            {link.BestCounty ? openBtn('County', link.BestCounty, `County: ${link.BestCounty}`) : null}
            {link.BestCity ? openBtn('City', link.BestCity, `City: ${link.BestCity}`) : null}
            {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`) : null}
          </div>
        </details>
      );
    }

    if (areaType === 'Metro' && (link.BestCounty || link.BestCity || link.BestZipcode || link.BestNeighborhood)) {
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {link.BestCounty ? openBtn('County', link.BestCounty, `County: ${link.BestCounty}`, link.BestCountyId || null) : null}
              {link.BestCity ? openBtn('City', link.BestCity, `City: ${link.BestCity}`, link.BestCityId || null) : null}
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`, link.BestZipcodeId || null) : null}
              {link.BestNeighborhood ? openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`, link.BestNeighborhoodId || null) : null}
          </div>
        </details>
      );
    }

    if (areaType === 'City' && (link.BestZipcode || link.BestNeighborhood)) {
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`, link.BestZipcodeId || null) : null}
              {link.BestNeighborhood ? openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`, link.BestNeighborhoodId || null) : null}
          </div>
        </details>
      );
    }

    if (areaType === 'County' && (link.BestCity || link.BestZipcode || link.BestNeighborhood)) {
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
            {link.BestCity ? openBtn('City', link.BestCity, `City: ${link.BestCity}`) : null}
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`, link.BestZipcodeId || null) : null}
              {link.BestNeighborhood ? openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`, link.BestNeighborhoodId || null) : null}
          </div>
        </details>
      );
    }

    if (areaType === 'Zipcode' && link.BestNeighborhood) {
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`, link.BestNeighborhoodId || null)}
          </div>
        </details>
      );
    }

      // Fallback UI if no specific child recommendations exist for this level
      return (
        <details>
          <summary>Explore…</summary>
          <div style={{ paddingTop: 6, color: '#666' }}>
            No recommended child areas for this selection.
          </div>
        </details>
      );
  };

  const displayAreaLabel = getAreaDisplayLabel(areaType);

  return (
    <>
      <table className="table" style={{ marginTop: "20px", borderCollapse: 'collapse', borderSpacing: 0 }}>
        <thead>
          <tr>
            <th>{displayAreaLabel}</th>
            <th>
              Price
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Last month price and the change (%) from the previous month</span>
              </span>
            </th>
            <th className="hide-sm">
              Price YoY
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Year-over-year price change (%) vs the same month last year</span>
              </span>
            </th>
            <th className="hide-sm">
              DOM
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Average number of days on market for a property to sell</span>
              </span>
            </th>
            <th className="hide-sm">
              Inventory
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Number of homes listed for sale (current month)</span>
              </span>
            </th>
            <th>
              Rent
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Last month rent and the change (%) from the previous month</span>
              </span>
            </th>
            <th className="hide-sm">
              Fcst 1m
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 1 month</span>
              </span>
            </th>
            <th className="hide-sm">
              Fcst 3m
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 3 months</span>
              </span>
            </th>
            <th className="hide-sm">
              Fcst 12m
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 12 months</span>
              </span>
            </th>
            <th>
              <div ref={dropdownRef} className="dropdown" style={{ position: 'relative', display: 'inline-block' }}>
                <button 
                  className="btn dropdown-toggle" 
                  style={{ padding: '8px 16px', fontSize: '16px', minWidth: '100px', fontWeight: 'bold' }}
                  title="Select chart type"
                  onClick={(e) => {
                    console.log(`[METRO_DATA_TABLE] Charts dropdown button clicked - SIMPLE TEST`);
                    e.preventDefault();
                    e.stopPropagation();
                    const dropdown = e.target.nextElementSibling;
                    console.log(`[METRO_DATA_TABLE] Dropdown element:`, dropdown);
                    console.log(`[METRO_DATA_TABLE] Current dropdown display:`, dropdown.style.display);
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                    console.log(`[METRO_DATA_TABLE] New dropdown display:`, dropdown.style.display);
                  }}
                >
                  Charts ▼
                </button>
                <div 
                  className="dropdown-menu" 
                  style={{ 
                    display: 'none', 
                    position: 'absolute', 
                    top: '100%', 
                    left: '0', 
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '120px'
                  }}
                >
                  <button 
                    className="dropdown-item" 
                    style={{ 
                      display: 'block', 
                      width: '100%', 
                      padding: '10px 16px', 
                      border: 'none', 
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      console.log(`[METRO_DATA_TABLE] Price button clicked - SIMPLE TEST`);
                      console.log(`[METRO_DATA_TABLE] showChart function:`, !!showChart);
                      console.log(`[METRO_DATA_TABLE] selectedMetro:`, selectedMetro);
                      if (showChart && selectedMetro) {
                        console.log(`[METRO_DATA_TABLE] Calling showChart('Price', '${selectedMetro}', RegionID=${currentMetro?.RegionID})`);
                        showChart('Price', selectedMetro, currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id);
                      } else {
                        console.log(`[METRO_DATA_TABLE] Cannot call showChart - missing requirements:`, {
                          hasShowChart: !!showChart,
                          hasSelectedMetro: !!selectedMetro
                        });
                      }
                      const dropdown = e.target.closest('.dropdown-menu');
                      if (dropdown) dropdown.style.display = 'none';
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Price
                  </button>
                  <button 
                    className="dropdown-item" 
                    style={{ 
                      display: 'block', 
                      width: '100%', 
                      padding: '10px 16px', 
                      border: 'none', 
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      position: 'relative',
                      zIndex: 1001
                    }}
                    onMouseDown={(e) => {
                      console.log(`[METRO_DATA_TABLE] 🖱️ Rent button MOUSEDOWN - Event registered!`);
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      console.log(`[METRO_DATA_TABLE] ✅✅✅ RENT BUTTON CLICKED - CLICK EVENT FIRED! ✅✅✅`);
                      console.log(`[METRO_DATA_TABLE] Event details:`, {
                        type: e.type,
                        target: e.target,
                        currentTarget: e.currentTarget,
                        button: e.button,
                        bubbles: e.bubbles
                      });
                      e.preventDefault();
                      e.stopPropagation();
                      console.log(`[METRO_DATA_TABLE] showChart function:`, !!showChart, typeof showChart);
                      console.log(`[METRO_DATA_TABLE] selectedMetro:`, selectedMetro);
                      if (showChart && selectedMetro) {
                        const regionId = currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id;
                        console.log(`[METRO_DATA_TABLE] ✅ Calling showChart('Rent', '${selectedMetro}', RegionID=${regionId})`);
                        showChart('Rent', selectedMetro, regionId);
                        console.log(`[METRO_DATA_TABLE] ✅ showChart call completed for Rent`);
                      } else {
                        console.log(`[METRO_DATA_TABLE] ❌ Cannot call showChart - missing requirements:`, {
                          hasShowChart: !!showChart,
                          hasSelectedMetro: !!selectedMetro
                        });
                      }
                      const dropdown = e.target.closest('.dropdown-menu');
                      if (dropdown) {
                        console.log(`[METRO_DATA_TABLE] Closing dropdown after Rent click`);
                        dropdown.style.display = 'none';
                      }
                    }}
                    onMouseEnter={(e) => {
                      console.log(`[METRO_DATA_TABLE] 🖱️ Mouse entered Rent button`);
                      e.target.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      console.log(`[METRO_DATA_TABLE] 🖱️ Mouse left Rent button`);
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    Rent
                  </button>
                  <button 
                    className="dropdown-item" 
                    style={{ 
                      display: 'block', 
                      width: '100%', 
                      padding: '10px 16px', 
                      border: 'none', 
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      console.log(`[METRO_DATA_TABLE] Inventory button clicked`);
                      if (showChart && selectedMetro) {
                        console.log(`[METRO_DATA_TABLE] Calling showChart('Inventory', '${selectedMetro}', RegionID=${currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id})`);
                        showChart('Inventory', selectedMetro, currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id);
                      } else {
                        console.log(`[METRO_DATA_TABLE] Cannot call showChart for Inventory - missing requirements`);
                      }
                      const dropdown = e.target.closest('.dropdown-menu');
                      if (dropdown) dropdown.style.display = 'none';
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Inventory
                  </button>
                  <button 
                    className="dropdown-item" 
                    style={{ 
                      display: 'block', 
                      width: '100%', 
                      padding: '10px 16px', 
                      border: 'none', 
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      console.log(`[METRO_DATA_TABLE] DOM button clicked`);
                      if (showChart && selectedMetro) {
                        console.log(`[METRO_DATA_TABLE] Calling showChart('DOM', '${selectedMetro}', RegionID=${currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id})`);
                        showChart('DOM', selectedMetro, currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id);
                      } else {
                        console.log(`[METRO_DATA_TABLE] Cannot call showChart for DOM - missing requirements`);
                      }
                      const dropdown = e.target.closest('.dropdown-menu');
                      if (dropdown) dropdown.style.display = 'none';
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    DOM
                  </button>
                </div>
              </div>
            </th>
            {showExplore ? (
              <th>
                Explore
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Additional exploration options and actions</span>
                </span>
              </th>
            ) : null}
            <th>Map</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{selectedMetro}</td>
            <td>
              {currentMetro.Price != null ? fmtNumber(currentMetro.Price, 0) : '-'}
              {renderDelta(currentMetro.PriceDelta, false, areaType)}
            </td>
            <td className="hide-sm">
              {renderDelta(currentMetro.PriceYoY, false, areaType)}
            </td>
            <td className="hide-sm">
              {currentMetro.Days != null ? fmtNumber(currentMetro.Days, 0) : '-'}
              {renderDelta(currentMetro.DaysDelta, true, areaType)}
            </td>
            <td className="hide-sm">
              {currentMetro.Inventory != null ? fmtNumber(currentMetro.Inventory, 0) : '-'}
              {renderDelta(currentMetro.InventoryDelta, false, areaType)}
            </td>
            <td>
              {currentMetro.Rent != null ? fmtNumber(currentMetro.Rent, 0) : '-'}
              {renderDelta(currentMetro.RentDelta, false, areaType)}
            </td>
            <td className="hide-sm">
              {currentMetro?.f1m ? `${Number(currentMetro.f1m).toFixed(1)}%` : '-'}
            </td>
            <td className="hide-sm">
              {currentMetro?.f3m ? `${Number(currentMetro.f3m).toFixed(1)}%` : '-'}
            </td>
            <td className="hide-sm">
              {currentMetro?.f12m ? `${Number(currentMetro.f12m).toFixed(1)}%` : '-'}
            </td>
            <td style={{ padding: '8px 2px 8px 8px' }}>
              {/* Charts functionality moved to header */}
            </td>
            {showExplore ? (
              <td style={{ padding: '8px 2px 8px 2px' }}>
                {renderExploreCell()}
              </td>
            ) : null}
            <td style={{ padding: '8px 8px 8px 2px' }}>
              <a
                  className="btn" 
                title="Open in Google Maps"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(selectedMetro))}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span role="img" aria-label="Open map">🗺️</span>
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default MetroDataTable;

